import { useState } from "react";
import Icon from "@/components/icon";
import { PdfViewerScreen } from "@/features/content/pdf-viewer";
import { downloadViaEitaa } from "@/utils/utilities";
import type { CourseContent } from "@/services/vitrin-service";

type Props = {
  content: CourseContent;
  /** Resolved file URL — used both for downloading and for the PDF preview. */
  url: string;
};

const getFileExtension = (fileName?: string) => {
  if (!fileName || !fileName.includes(".")) return "";
  const ext = fileName.split(".").pop() ?? "";
  return ext.length <= 5 ? ext.toUpperCase() : "";
};

const formatFileSize = (bytes?: number) => {
  if (!bytes || bytes <= 0) return "";
  const units = ["بایت", "کیلوبایت", "مگابایت", "گیگابایت"];
  let value = bytes;
  let unitIndex = 0;
  while (value >= 1024 && unitIndex < units.length - 1) {
    value /= 1024;
    unitIndex += 1;
  }
  const rounded =
    value >= 10 || unitIndex === 0 ? Math.round(value) : Math.round(value * 10) / 10;
  return `${rounded.toLocaleString("fa-IR")} ${units[unitIndex]}`;
};

const isPdfContent = (content: CourseContent, url: string) => {
  const attachment = content.attachments?.[0];
  return (
    attachment?.mime_type === "application/pdf" ||
    /\.pdf(\?|#|$)/i.test(attachment?.file_name || "") ||
    /\.pdf(\?|#|$)/i.test(url) ||
    /pdf/i.test(content.external_media?.media_type || "")
  );
};

/**
 * A lesson file attachment row. It derives the file's name, size and type from
 * `content`, downloads through the Eitaa bridge, and — for PDFs — opens a
 * full-screen, view-only reader.
 */
export default function LessonFile({ content, url }: Props) {
  const [viewerOpen, setViewerOpen] = useState(false);

  const attachment = content.attachments?.[0];
  const fileName = attachment?.file_name || content.type_label || "فایل پیوست";
  const fileExt = getFileExtension(fileName);
  const sizeLabel = formatFileSize(attachment?.size);
  const isPdf = isPdfContent(content, url);

  return (
    <div className="flex items-center gap-3 rounded-2xl border border-content3 bg-background p-3">
      <span className="flex size-12 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
        {fileExt ? (
          <span className="text-[11px] font-bold tracking-tight">{fileExt}</span>
        ) : (
          <Icon name="document-text-bold" size={22} className="text-primary" />
        )}
      </span>

      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium text-default-800">{fileName}</p>
        <p className="mt-0.5 text-xs text-default-500">{sizeLabel || "فایل پیوست"}</p>
      </div>

      <div className="flex shrink-0 items-center gap-2">
        {isPdf ? (
          <button
            type="button"
            onClick={() => setViewerOpen(true)}
            className="rounded-lg bg-primary px-3 py-2 text-xs font-semibold text-white"
          >
            مشاهده
          </button>
        ) : null}

        <button
          type="button"
          onClick={() => downloadViaEitaa(url, fileName)}
          aria-label="دانلود"
          className="flex size-9 items-center justify-center rounded-full bg-content3 text-default-700 transition-colors hover:bg-primary hover:text-white"
        >
          <Icon name="download" size={18} />
        </button>
      </div>

      {isPdf && viewerOpen ? (
        <PdfViewerScreen url={url} title={fileName} onClose={() => setViewerOpen(false)} />
      ) : null}
    </div>
  );
}
